import React, { ReactElement, useEffect, useLayoutEffect } from 'react';
import { useDispatchLoginKeyAction } from 'login/store/dispatcher';
import { useLoginStateSelector } from 'login/store/selectors';
import { AppContextProvider } from 'app/AppContext';
import { LoginStatus } from 'login/store/LoginState';
import { useDispatchStreamParmUpdates } from './store/dispatcher';
import { AppMode } from 'app/store/general/AppMode';
import { getGlobalWindowVariables } from 'utils/globalWindowVariables';
import Services from 'store/Services';
import { useNetworkConnectivityStatus } from 'app/store/connectivity/selectors';
import { newportLoginErrors } from './Login/NewportLoginErrorText';
import IndigoServiceClient from 'communication/IndigoServiceClient';
import EmbeddedLoginError from 'embedded/Login/EmbeddedLoginError';
import { useReloadRequired } from 'embedded/api/hooks/useReloadRequired';
import EmbeddedApp from 'embedded/EmbeddedApp';
import { useDispatch } from 'store/hooks';
import { fetchNewportVersionInfoThunk } from 'newport/store/thunks';

export interface NewportAppProps {
    layout: string;
    mode: AppMode.EMBEDDED_SINGLE_WIDGET | AppMode.EMBEDDED_DASHBOARD;
    services: Services;
}

export default function NewportApp(props: NewportAppProps): ReactElement {
    const { mode, services, layout } = props;
    const { loggingService, indigoServiceClient } = services;

    const loginState = useLoginStateSelector();
    const { status: loginStatus } = loginState;

    const networkConnectivityStatus = useNetworkConnectivityStatus();
    const lostNetworkConnection =
        networkConnectivityStatus.lostConnectionStatus;

    const globals = getGlobalWindowVariables();
    const { errorCode } = globals;
    const { reloadPossible } = useReloadRequired();

    const dispatch = useDispatch();
    const dispatchLogin = useDispatchLoginKeyAction();
    const dispatchStreamParmUpdates = useDispatchStreamParmUpdates();
    const loginKey = globals.key;

    const onInitialiseStore = (panelId: string, mode: AppMode) => {
        if (!panelId && mode !== AppMode.EMBEDDED_SINGLE_WIDGET) {
            loggingService.warn(
                'NewportApp',
                'Newport linked palette without panelId',
                window.location.toString()
            );
        }
    };

    useEffect(() => {
        dispatch(fetchNewportVersionInfoThunk());
    }, [dispatch]);

    useLayoutEffect(() => {
        if (lostNetworkConnection) {
            return;
        }
        if (errorCode) {
            return;
        }
        if (!loginKey) {
            return;
        }
        if (globals.hasAllJwtAuthenticationDetails) {
            // EmbeddedApp will handle login...
            return;
        }

        if (loginStatus === LoginStatus.NONE) {
            dispatchLogin(loginKey);
        } else if (loginStatus === LoginStatus.SUCCESS) {
            dispatchStreamParmUpdates(loginKey);
        }
    }, [
        loginKey,
        dispatchLogin,
        dispatchStreamParmUpdates,
        loginStatus,
        lostNetworkConnection,
        errorCode,
        globals.hasAllJwtAuthenticationDetails,
    ]);

    const onReconnected = () => {
        if (loginKey) {
            dispatchLogin(loginKey);
            return { handled: true };
        }
        return { handled: false };
    };

    if (!validateKeyAndSetSessionId(loginKey, indigoServiceClient)) {
        return (
            <AppContextProvider services={services}>
                <EmbeddedLoginError
                    text={newportLoginErrors(reloadPossible).invalidKey}
                />
            </AppContextProvider>
        );
    }

    const suppressRequestedHostReauthenticates = !!loginKey;

    return (
        <EmbeddedApp
            layout={layout}
            mode={mode}
            services={services}
            suppressRequestedHostReauthenticates={
                suppressRequestedHostReauthenticates
            }
            onReconnected={onReconnected}
            onInitialiseStore={onInitialiseStore}
        />
    );

    function validateKeyAndSetSessionId(
        loginKey: string | undefined,
        _indigoServiceClient: IndigoServiceClient
    ) {
        if (globals.hasAllJwtAuthenticationDetails) {
            // We will use the JWT token in EmbeddedApp...
            return true;
        }

        if (!loginKey || loginKey.trim() === '') {
            loggingService.critical(
                'NewportApp',
                'Key not received from Newport',
                window.location.toString()
            );
            return false;
        }

        const parts = loginKey.split(':');
        if (parts.length !== 3) {
            loggingService.critical(
                'NewportApp',
                'Unexpected key format',
                window.location.toString()
            );
            return false;
        }

        return true;
    }
}
